import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { Dispatch } from '@tmapy/redux';
import { useMessage, useSelectMessages } from '@tmapy/intl';
import { unaccentAndLower } from '@tmapy/beetlejuice-utils';

import {
  BtnBar,
  Form,
  FormItem,
  FormRow,
  Input,
  CtaBtn,
  SecondaryBtn,
  StickyFooter,
  MultiSelectProps,
  MultiSelect,
  Select,
} from '@tmapy/style-guide';

import type { InputProps } from '../../hooks/useInput';
import { useInput, useMultiSelect } from '../../hooks';
import { OmittedFormItemProps } from '../../types';
import { setCheckListFilter } from '../../redux/filter/actions';

import type { DocsListQuery, DocsListResponse } from '../../openapi/operations/docsList';
import { DocCheckStateEnum } from '../../openapi/types/DocCheckStateEnum';

import { checkSourceEnum } from '../constants';
import { docFormMessages, docFilterMessages } from './docsMessages';
import { checkSourceMessages, checkStateMessages } from './checkMessages';

type CheckFiltersProps = {
  filters: DocsListQuery;
  dispatch: Dispatch;
};

/**
 * Vzniklo jako kopie DocFilter
 */
export const CheckFilter: React.FC<CheckFiltersProps> = ({ filters, dispatch }) => {
  const formatMessage = useMessage();

  const id = useInput<InputProps, number>({
    name: 'id',
    initialValue: filters.id,
    label: formatMessage(docFormMessages.id),
  });

  const name = useInput<InputProps>({
    name: 'name__icontains',
    initialValue: filters.name__icontains,
    label: formatMessage(docFormMessages.name),
  });

  const dmvs_id_podani = useInput<InputProps>({
    name: 'dmvs_id_podani',
    initialValue: filters.dmvs_id_podani,
    label: formatMessage(docFormMessages.dmvs_id_podani),
  });

  const [createdGte, setCreateGt] = useState(filters.created__gte);
  const created__gte = {
    name: 'created__gte',
    clear: () => {
      setCreateGt('');
    },
  };

  const handleCreateGtChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setCreateGt(eventData.value);
  };

  const [createdLt, setCreateLt] = useState(filters.created__lt);
  const created__lt = {
    name: 'created__lt',
    clear: () => {
      setCreateLt('');
    },
  };

  const handleCreateLtChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setCreateLt(eventData.value);
  };

  const creatorFullName = useInput<InputProps>({
    name: 'creator_full_name',
    initialValue: filters.creator_full_name,
    label: formatMessage(docFormMessages.creatorDtmFullName),
  });

  const geodesistFullName = useInput<InputProps>({
    name: 'geodesist_full_name',
    initialValue: filters.geodesist_full_name,
    label: formatMessage(docFormMessages.geodesist),
  });

  // LATEST CHECK STATE
  const stateItems = useSelectMessages(checkStateMessages, DocCheckStateEnum);

  const prepareStateItems = useCallback(
    (val: DocCheckStateEnum) => {
      return stateItems.filter(
        (state) => unaccentAndLower(state.label).indexOf(unaccentAndLower(val)) >= 0,
      );
    },
    [stateItems],
  );

  const stateInitialValues = useMemo(() => {
    return filters.latest_check_state__in?.length
      ? stateItems.filter((state) => filters.latest_check_state__in!.includes(state.value))
      : [];
  }, [stateItems, filters.latest_check_state__in]);

  const latest_check_state__in = useMultiSelect<
    MultiSelectProps<DocsListResponse> | OmittedFormItemProps,
    (typeof stateItems)[0]
  >({
    name: 'latest_check_state__in',
    label: formatMessage(docFormMessages.latestCheckState),
    initialValue: stateInitialValues,
    prepareItems: prepareStateItems,
    renderValue: (state: (typeof stateItems)[0]) => state.label,
    renderItem: (state: (typeof stateItems)[0]) => state.label,
  });

  // CHECK SOURCE
  const checkSourceItems = useSelectMessages(checkSourceMessages, checkSourceEnum);
  const check_source = useInput({
    name: 'check_source',
    initialValue:
      filters.has_dmvs_source === true
        ? checkSourceEnum.ISDMVS
        : filters.has_dmvs_source === false
          ? checkSourceEnum.ISDTMK
          : '',
    items: checkSourceItems,
    label: formatMessage(docFormMessages.check_source),
  });

  // CLEAR SCOPE
  const filterClearScope = [
    id,
    name,
    created__gte,
    created__lt,
    dmvs_id_podani,
    latest_check_state__in,
    creatorFullName,
    geodesistFullName,
    check_source,
  ];

  function handleClearBtnClick() {
    filterClearScope.forEach((formItem) => formItem?.clear?.());
    dispatch(setCheckListFilter({}));
  }

  function handleSubmit() {
    dispatch(
      setCheckListFilter({
        id: id.value,
        name__icontains: name.value,
        dmvs_id_podani: dmvs_id_podani.value,
        created__gte: createdGte,
        created__lt: createdLt,
        latest_check_state__in: latest_check_state__in.values.map((state) => state.value),
        creator_full_name: creatorFullName.value,
        geodesist_full_name: geodesistFullName.value,
        has_dmvs_source:
          check_source.value === checkSourceEnum.ISDMVS
            ? true
            : check_source.value === checkSourceEnum.ISDTMK
              ? false
              : undefined,
      }),
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <div className='sg-u-vs-2'>
        <FormRow>
          <FormItem {...id}>
            <Input {...id} />
          </FormItem>
          <FormItem {...latest_check_state__in}>
            <MultiSelect {...latest_check_state__in} />
          </FormItem>
        </FormRow>

        <FormRow>
          <FormItem {...check_source}>
            <Select hasEmptyItem {...check_source} />
          </FormItem>
          <FormItem {...dmvs_id_podani}>
            <Input {...dmvs_id_podani} />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem {...name}>
            <Input {...name} />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem label={formatMessage(docFormMessages.createdGte)}>
            <Input
              type='date'
              name='created__gte'
              value={createdGte}
              onChange={handleCreateGtChange}
            />
          </FormItem>
          <FormItem label={formatMessage(docFormMessages.createdLt)}>
            <Input
              type='date'
              name='created__lt'
              value={createdLt}
              onChange={handleCreateLtChange}
            />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem {...creatorFullName}>
            <Input {...creatorFullName} />
          </FormItem>
          <FormItem {...geodesistFullName}>
            <Input {...geodesistFullName} />
          </FormItem>
        </FormRow>
      </div>
      <StickyFooter>
        <BtnBar stretch>
          <CtaBtn type='submit'>
            <FormattedMessage {...docFilterMessages.submitBtn} />
          </CtaBtn>
          <SecondaryBtn isDanger onClick={handleClearBtnClick}>
            <FormattedMessage {...docFilterMessages.clearBtn} />
          </SecondaryBtn>
        </BtnBar>
      </StickyFooter>
    </Form>
  );
};
